// Finance Command Center — Natural Language Interface (Polished)
// Additions: command history (localStorage), example prompts, export,
// summary bar, multi-turn follow-up.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField,
    IconButton, Tooltip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, fmtMoney,
} from './AgentUIKit';
import {
    nlpTransactionEntry, nlpBillEntry, nlpFinancialQuery,
    nlpWhatIf, nlpReportBuilder, nlpCommandSummary,
} from './api';

const TABS = ['Transaction Entry', 'Bill Entry', 'Financial Query', 'What-If Analysis', 'Report Builder', 'Capabilities'];

const HISTORY_KEY = 'finance.nlp.history.v1';
const loadHistory = () => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
    catch { return []; }
};
const pushHistory = (tab, command, response) => {
    const hist = loadHistory();
    hist.unshift({ id: Date.now(), tab, command, response, ts: new Date().toISOString() });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(hist.slice(0, 50)));
};

const EXAMPLES = {
    0: [
        'Record $500 payment from Acme Corp for consulting services',
        'Received $2,100 from Client X for invoice #INV-042',
        'Record $45 in office supplies purchased from Staples',
    ],
    1: [
        'Create bill for $1,200 from Office Depot for supplies, due in 30 days',
        'AWS cloud hosting bill $345 for January, due Net 15',
        'Quarterly accountant fees $2,500 from Smith & Co, due 2026-05-15',
    ],
    2: [
        'What was our total revenue last quarter compared to the same period last year?',
        'Show top 5 vendors by spend this month',
        'What is our current DSO and how does it trend over the last 6 months?',
    ],
    3: [
        'What if we lose our top 3 clients and reduce marketing spend by 20%?',
        'What if we hire 5 new engineers at $150K each starting next quarter?',
        'What if AR collections slow by 15 days and we have a $200K unexpected expense?',
    ],
    4: [
        'Monthly P&L with department breakdown and YoY comparison',
        'Weekly cash flow forecast for next 90 days with scenarios',
        'Vendor concentration report with Pareto analysis',
    ],
};

function exportJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function FinanceCommandCenter() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState(loadHistory());
    const [showHistory, setShowHistory] = useState(false);

    const [txnCommand, setTxnCommand] = useState('');
    const [txnResult, setTxnResult] = useState(null);
    const [billCommand, setBillCommand] = useState('');
    const [billResult, setBillResult] = useState(null);
    const [queryText, setQueryText] = useState('');
    const [queryResult, setQueryResult] = useState(null);
    const [scenario, setScenario] = useState('');
    const [whatIfResult, setWhatIfResult] = useState(null);
    const [reportDesc, setReportDesc] = useState('');
    const [reportResult, setReportResult] = useState(null);
    const [capabilities, setCapabilities] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (tab === 5 && !capabilities) {
            run(() => nlpCommandSummary()).then(r => r?.data?.success && setCapabilities(r.data.data));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const recordHistory = (tabIdx, command, response) => {
        pushHistory(tabIdx, command, response);
        setHistory(loadHistory());
    };

    const clearHistory = () => {
        localStorage.removeItem(HISTORY_KEY);
        setHistory([]);
    };

    const summaryCards = [
        { label: 'Recent Commands', value: history.length, accent: 'primary' },
        { label: 'Capabilities', value: capabilities ? Object.keys(capabilities.categories || capabilities).length : '—', accent: 'info' },
        { label: 'Queries This Session', value: history.filter(h => h.tab === 2).length, accent: 'secondary' },
        { label: 'What-Ifs This Session', value: history.filter(h => h.tab === 3).length, accent: 'warning' },
    ];

    const renderExamples = (tabIdx, setter) => (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Examples:</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(EXAMPLES[tabIdx] || []).map((ex, i) => (
                    <Chip key={i} size="small" variant="outlined" clickable label={ex}
                        onClick={() => setter(ex)} sx={{ mb: 0.5 }} />
                ))}
            </Stack>
        </Box>
    );

    const renderExport = (data, filename) => (
        <Tooltip title="Export JSON">
            <IconButton size="small" onClick={() => exportJson(data, filename)}>
                <DownloadIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Conversational Transaction Entry" subtitle="Describe a transaction in natural language" icon={<SmartToyIcon fontSize="small" color="info" />} />
                        <TextField
                            label="Transaction command"
                            placeholder={EXAMPLES[0][0]}
                            multiline rows={3} fullWidth size="small"
                            value={txnCommand} onChange={e => setTxnCommand(e.target.value)}
                        />
                        {renderExamples(0, setTxnCommand)}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!txnCommand.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => nlpTransactionEntry(txnCommand));
                                    if (r?.data?.success) { setTxnResult(r.data.data); recordHistory(0, txnCommand, r.data.data); }
                                }}>Process</Button>
                            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
                                onClick={() => { setTxnCommand(''); setTxnResult(null); }}>Clear</Button>
                        </Stack>
                        {txnResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {txnResult.action && <Chip size="small" label={`Action: ${txnResult.action}`} color="primary" />}
                                        {txnResult.amount != null && <Chip size="small" label={`Amount: ${fmtMoney(txnResult.amount)}`} variant="outlined" />}
                                        {txnResult.debit_account && <Chip size="small" label={`Dr: ${txnResult.debit_account}`} variant="outlined" />}
                                        {txnResult.credit_account && <Chip size="small" label={`Cr: ${txnResult.credit_account}`} variant="outlined" />}
                                    </Stack>
                                    {renderExport(txnResult, 'transaction_entry.json')}
                                </Stack>
                                <AIAnalysisBox text={txnResult.explanation} title="Parsed Transaction" />
                                {txnResult.journal_lines?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Journal Lines</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'account', label: 'Account', render: r => r.account || r.account_name },
                                                { key: 'debit', label: 'DR', align: 'right', render: r => r.debit ? fmtMoney(r.debit) : '—' },
                                                { key: 'credit', label: 'CR', align: 'right', render: r => r.credit ? fmtMoney(r.credit) : '—' },
                                                { key: 'memo', label: 'Memo' },
                                            ]}
                                            rows={txnResult.journal_lines}
                                        />
                                    </Box>
                                )}
                            </Paper>
                        )}
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Conversational Bill Entry" icon={<SmartToyIcon fontSize="small" color="info" />} />
                        <TextField
                            label="Bill command"
                            placeholder={EXAMPLES[1][0]}
                            fullWidth size="small"
                            value={billCommand} onChange={e => setBillCommand(e.target.value)}
                        />
                        {renderExamples(1, setBillCommand)}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!billCommand.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => nlpBillEntry(billCommand));
                                    if (r?.data?.success) { setBillResult(r.data.data); recordHistory(1, billCommand, r.data.data); }
                                }}>Process</Button>
                            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
                                onClick={() => { setBillCommand(''); setBillResult(null); }}>Clear</Button>
                        </Stack>
                        {billResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Typography variant="caption" fontWeight={600}>Extracted Fields</Typography>
                                    {renderExport(billResult, 'bill_entry.json')}
                                </Stack>
                                <SummaryGrid cards={[
                                    { label: 'Vendor', value: billResult.vendor || '—', accent: 'primary' },
                                    { label: 'Amount', value: fmtMoney(billResult.amount), accent: 'warning' },
                                    { label: 'Category', value: billResult.category || '—', accent: 'info' },
                                    { label: 'Due Date', value: billResult.due_date || '—', accent: 'secondary' },
                                ]} />
                                <AIAnalysisBox text={billResult.explanation} title="Parsed Bill Details" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Financial Query" subtitle="Ask questions about your financial data in plain English" icon={<SmartToyIcon fontSize="small" color="info" />} />
                        <TextField
                            label="Your question"
                            placeholder={EXAMPLES[2][0]}
                            fullWidth size="small" multiline rows={2}
                            value={queryText} onChange={e => setQueryText(e.target.value)}
                        />
                        {renderExamples(2, setQueryText)}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!queryText.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => nlpFinancialQuery(queryText));
                                    if (r?.data?.success) { setQueryResult(r.data.data); recordHistory(2, queryText, r.data.data); }
                                }}>Ask</Button>
                            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
                                onClick={() => { setQueryText(''); setQueryResult(null); }}>Clear</Button>
                        </Stack>
                        {queryResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="flex-end">
                                    {renderExport(queryResult, 'query_result.json')}
                                </Stack>
                                <AIAnalysisBox text={queryResult.answer} title="Answer" />
                                {queryResult.data_points?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Key Data Points</Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                            {queryResult.data_points.map((dp, i) => (
                                                <Chip key={i} size="small" label={typeof dp === 'string' ? dp : `${dp.label}: ${dp.value}`} variant="outlined" />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                {queryResult.follow_up_suggestions?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Follow-up Questions</Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                            {queryResult.follow_up_suggestions.map((s, i) => (
                                                <Button key={i} size="small" variant="outlined" sx={{ textTransform: 'none' }}
                                                    onClick={() => { setQueryText(s); setQueryResult(null); }}>
                                                    {s}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Paper>
                        )}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="What-If Scenario Analysis" icon={<SmartToyIcon fontSize="small" color="info" />} />
                        <TextField
                            label="Describe scenario"
                            placeholder={EXAMPLES[3][0]}
                            multiline rows={3} fullWidth size="small"
                            value={scenario} onChange={e => setScenario(e.target.value)}
                        />
                        {renderExamples(3, setScenario)}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!scenario.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => nlpWhatIf(scenario));
                                    if (r?.data?.success) { setWhatIfResult(r.data.data); recordHistory(3, scenario, r.data.data); }
                                }}>Analyze</Button>
                            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
                                onClick={() => { setScenario(''); setWhatIfResult(null); }}>Clear</Button>
                        </Stack>
                        {whatIfResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="flex-end">
                                    {renderExport(whatIfResult, 'what_if.json')}
                                </Stack>
                                <SummaryGrid cards={[
                                    { label: 'Revenue Change', value: fmtMoney(whatIfResult.revenue_change), accent: whatIfResult.revenue_change >= 0 ? 'success' : 'danger' },
                                    { label: 'Expense Change', value: fmtMoney(whatIfResult.expense_change), accent: whatIfResult.expense_change <= 0 ? 'success' : 'warning' },
                                    { label: 'Runway Change', value: (whatIfResult.runway_change ?? whatIfResult.runway_change_months) != null ? `${whatIfResult.runway_change ?? whatIfResult.runway_change_months} mo` : '—', accent: 'info' },
                                ]} cols={3} />
                                {whatIfResult.risks?.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(213,43,30,0.05)', borderLeft: '3px solid', borderColor: 'error.main' }}>
                                        <Typography variant="caption" fontWeight={600} color="error.main">Risks</Typography>
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                            {whatIfResult.risks.map((r, i) => (
                                                <Typography key={i} variant="body2">• {typeof r === 'string' ? r : r.description}</Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}
                                {whatIfResult.recommendations?.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(44,160,28,0.05)', borderLeft: '3px solid', borderColor: 'success.main' }}>
                                        <Typography variant="caption" fontWeight={600} color="success.main">Recommendations</Typography>
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                            {whatIfResult.recommendations.map((r, i) => (
                                                <Typography key={i} variant="body2">• {typeof r === 'string' ? r : r.description}</Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}
                                <AIAnalysisBox text={whatIfResult.ai_summary} title="AI Scenario Analysis" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 4:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Report Builder" subtitle="Describe the report, get a spec" icon={<SmartToyIcon fontSize="small" color="info" />} />
                        <TextField
                            label="Report description"
                            placeholder={EXAMPLES[4][0]}
                            fullWidth size="small" multiline rows={2}
                            value={reportDesc} onChange={e => setReportDesc(e.target.value)}
                        />
                        {renderExamples(4, setReportDesc)}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!reportDesc.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => nlpReportBuilder(reportDesc));
                                    if (r?.data?.success) { setReportResult(r.data.data); recordHistory(4, reportDesc, r.data.data); }
                                }}>Generate Spec</Button>
                            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
                                onClick={() => { setReportDesc(''); setReportResult(null); }}>Clear</Button>
                        </Stack>
                        {reportResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="flex-end">
                                    {renderExport(reportResult, 'report_spec.json')}
                                </Stack>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Paper variant="outlined" sx={{ p: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Title</Typography>
                                            <Typography variant="body1" fontWeight={600}>{reportResult.title || '—'}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Paper variant="outlined" sx={{ p: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Type</Typography>
                                            <Typography variant="body1" fontWeight={600}>{reportResult.type || '—'}</Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                                {reportResult.sections?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Report Sections</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'n', label: '#', render: (_, i) => i + 1 },
                                                { key: 'name', label: 'Section', render: r => r.name || r.title || r },
                                                { key: 'description', label: 'Description' },
                                            ]}
                                            rows={reportResult.sections}
                                        />
                                    </Box>
                                )}
                                <AIAnalysisBox text={reportResult.ai_summary} title="Report Specification" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 5:
                return capabilities ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Available Capabilities" subtitle="What the NL agent can do" />
                        {Object.entries(capabilities.categories || capabilities).map(([category, items]) => (
                            <Paper key={category} variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                                    {category.replace(/_/g, ' ')}
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                    {(Array.isArray(items) ? items : [items]).map((item, i) => (
                                        <Typography key={i} component="li" variant="body2">
                                            {typeof item === 'string' ? item : item.name || item.description}
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Finance Command Center"
            subtitle="Natural Language Command — conversational accounting, queries, what-if, report builder. Powered by Gemini."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Box sx={{ flex: 1 }}>
                        <SummaryGrid cards={summaryCards} />
                    </Box>
                    <Tooltip title={showHistory ? 'Hide history' : 'Show history'}>
                        <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
                {showHistory && (
                    <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 240, overflow: 'auto' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight={600}>Command History ({history.length})</Typography>
                            <Button size="small" onClick={clearHistory}>Clear</Button>
                        </Stack>
                        {history.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No history yet.</Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {history.slice(0, 15).map(h => (
                                    <Paper key={h.id} variant="outlined" sx={{ p: 0.75, bgcolor: 'action.hover' }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="small" label={TABS[h.tab]} variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(h.ts).toLocaleString()}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: 13 }}>{h.command}</Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Paper>
                )}
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
